/**
 * HH-5: the optimistic mutation queue's pure core — plain data in, plain
 * data out, zero dependencies (no api, no i18n, no window, no hidden
 * clock: callers pass `nowIso` in, the established nowMs-as-parameter
 * convention). The glue layer (shoplist-sync.js) owns every side effect:
 * sending, timers, listeners, toasts.
 *
 * The queue covers exactly the two op kinds the brief allows — checkoff/
 * uncheck and item add — and encodes the brief's written conflict rules:
 * checkoff is last-write-wins, both-checked is not a conflict, poll
 * responses merge (never clobber) items with queued local ops, and a
 * queued op the server reports impossible is dropped with a report,
 * never a blocking error. shoplist-queue.test.mjs is the executable form
 * of this contract and was written before this file existed.
 *
 * State is treated as immutable: every transition returns a new state
 * object (or the same reference when nothing changed, so callers can
 * cheaply detect no-ops). Ids are compared loosely-by-string throughout
 * (idEq): server rows arrive with string ids ($wpdb rows are stringly
 * typed), while POST /items responses carry integer insert ids — mixing
 * `===` on those would silently never match.
 */

function idEq(a, b) {
  return String(a) === String(b);
}

/**
 * The freshest queued intent for a row, applied over server truth. The
 * LAST matching op wins — there can be an older inflight op and a newer
 * pending one for the same item, and the newer one is what the user
 * currently sees and means.
 */
function overlayFromQueue(row, queue) {
  let intent = null;
  for (const op of queue) {
    if (op.kind === 'check' && idEq(op.itemId, row.id)) {
      intent = op;
    }
  }
  if (!intent) {
    return row;
  }
  return intent.checked
    ? { ...row, checked_at: intent.appliedAt }
    : { ...row, checked_at: null, checked_by: null };
}

export function createState(serverItems) {
  return {
    items: (serverItems || []).map((r) => ({ ...r })),
    queue: [],
    // Bumped on every ack. A poll captures ackSeq when its request STARTS;
    // if an ack lands while the poll is in flight, the poll's response was
    // read from a server state that may predate the acked write, and
    // mergeServerItems discards it whole. This is the guard for the one
    // interleaving no per-item rule can catch: tap → flush → ack landing
    // entirely inside another request's round trip.
    ackSeq: 0,
    nextTempSeq: 1,
    nextOpSeq: 1,
  };
}

export function hasPendingOps(state) {
  return state.queue.length > 0;
}

/**
 * Optimistic checkoff/uncheck: the item flips in the local view at once
 * and one op queues behind any already-pending work. A second toggle of
 * the same item coalesces into its still-pending op (latest value, same
 * queue position) — an op that is already inflight is never rewritten,
 * the follow-up toggle queues behind it instead.
 *
 * Coalescing targets the LAST pending op for the item, never the first
 * (review finding F1): a network failOp can legally leave TWO pending
 * ops for one item (the formerly-inflight head restored in front of a
 * later toggle), and coalescing into the older one would let an older
 * intent flush after the newer one — last-write-wins inverted, the
 * user's final tap silently reverted. Last-match also keeps the
 * coalesce target aligned with overlayFromQueue's own last-op-wins rule.
 */
export function applyCheck(state, itemId, checked, nowIso) {
  const item = state.items.find((i) => idEq(i.id, itemId));
  if (!item) {
    return state;
  }

  const items = state.items.map((i) => {
    if (!idEq(i.id, itemId)) {
      return i;
    }
    return checked ? { ...i, checked_at: nowIso } : { ...i, checked_at: null, checked_by: null };
  });

  let pendingIdx = -1;
  for (let i = 0; i < state.queue.length; i++) {
    const op = state.queue[i];
    if (op.kind === 'check' && idEq(op.itemId, itemId) && op.status === 'pending') {
      pendingIdx = i;
    }
  }

  if (pendingIdx >= 0) {
    const queue = state.queue.map((op, i) =>
      i === pendingIdx ? { ...op, checked, appliedAt: nowIso } : op
    );
    return { ...state, items, queue };
  }

  const op = {
    opId: 'op-' + state.nextOpSeq,
    kind: 'check',
    itemId,
    checked,
    label: item.label,
    appliedAt: nowIso,
    status: 'pending',
  };
  return { ...state, items, queue: [...state.queue, op], nextOpSeq: state.nextOpSeq + 1 };
}

/**
 * Optimistic add: a temp row (id 'tmp-N', marked `temp`) appears at the
 * end of the list at once. The op carries the caller-provided clientKey —
 * the server dedupes on it, which is what makes a lost-response retry
 * idempotent instead of a duplicate row.
 */
export function applyAdd(state, label, clientKey, nowIso) {
  const tempId = 'tmp-' + state.nextTempSeq;
  const maxPos = state.items.reduce((m, i) => Math.max(m, Number(i.position) || 0), -1);
  const row = {
    id: tempId,
    temp: true,
    label,
    note: null,
    position: maxPos + 1,
    checked_at: null,
    checked_by: null,
    client_key: clientKey,
    created_at: nowIso,
  };
  const op = {
    opId: 'op-' + state.nextOpSeq,
    kind: 'add',
    tempId,
    label,
    clientKey,
    appliedAt: nowIso,
    status: 'pending',
  };
  return {
    state: {
      ...state,
      items: [...state.items, row],
      queue: [...state.queue, op],
      nextTempSeq: state.nextTempSeq + 1,
      nextOpSeq: state.nextOpSeq + 1,
    },
    tempId,
  };
}

/**
 * Hands out the head op for sending and marks it inflight — strictly one
 * at a time, strictly FIFO. Refusing while any op is inflight is what
 * makes a double flush (online + focus + interval firing together)
 * structurally unable to double-send: there is no second op to take.
 *
 * One documented exception to the structural half (review finding F4):
 * when a merge folds an INFLIGHT add (the server provably already has
 * the row), that op leaves the queue while its HTTP request is still on
 * the wire, and takeNext may hand out the next op alongside it. That
 * racing request is a server-side dedupe no-op whose late response hits
 * the tolerant unknown-opId paths; serialization of actual sends in
 * exactly that window is the job of the glue's single shared flush
 * loop (shoplist-sync.js), not this function's.
 */
export function takeNext(state) {
  if (!state.queue.length || state.queue.some((op) => op.status === 'inflight')) {
    return { state, op: null };
  }
  const queue = state.queue.map((op, i) => (i === 0 ? { ...op, status: 'inflight' } : op));
  return { state: { ...state, queue }, op: queue[0] };
}

export function ackCheck(state, opId) {
  if (!state.queue.some((o) => o.opId === opId)) {
    return state;
  }
  return {
    ...state,
    queue: state.queue.filter((o) => o.opId !== opId),
    ackSeq: state.ackSeq + 1,
  };
}

/**
 * The add landed: adopt the server id onto the temp row and remap every
 * queued op that referenced the temp id (a checkoff done before the add
 * ever synced). If a poll already delivered the server row (a retried
 * add whose first attempt landed but whose response was lost), the temp
 * row folds into it instead of duplicating — the surviving row re-reads
 * its overlay from the (remapped) queue so a local checkoff isn't lost
 * in the fold.
 */
export function ackAdd(state, opId, serverItemId) {
  const op = state.queue.find((o) => o.opId === opId);
  if (!op) {
    return state;
  }

  const queue = state.queue
    .filter((o) => o.opId !== opId)
    .map((o) => (o.kind === 'check' && idEq(o.itemId, op.tempId) ? { ...o, itemId: serverItemId } : o));

  const serverRowPresent = state.items.some((i) => idEq(i.id, serverItemId) && !i.temp);
  let items;
  if (serverRowPresent) {
    items = state.items
      .filter((i) => !idEq(i.id, op.tempId))
      .map((i) => (idEq(i.id, serverItemId) ? overlayFromQueue(i, queue) : i));
  } else {
    items = state.items.map((i) => {
      if (!idEq(i.id, op.tempId)) {
        return i;
      }
      const { temp, ...rest } = i;
      return { ...rest, id: serverItemId };
    });
  }

  return { ...state, items, queue, ackSeq: state.ackSeq + 1 };
}

/**
 * A send failed. Network-class failures (offline, 5xx, timeouts) return
 * the op to pending at its original queue position — the next flush
 * trigger retries it first, order preserved. Impossible-class failures
 * (the server answered: this can never succeed — item deleted elsewhere,
 * list gone) drop the op, cascade over every other queued op targeting
 * the same row, remove the row from the local view, and report the
 * dropped ops so the glue can toast — never a blocking error.
 */
export function failOp(state, opId, { impossible = false } = {}) {
  const op = state.queue.find((o) => o.opId === opId);
  if (!op) {
    return { state, dropped: [] };
  }

  if (!impossible) {
    return {
      state: {
        ...state,
        queue: state.queue.map((o) => (o.opId === opId ? { ...o, status: 'pending' } : o)),
      },
      dropped: [],
    };
  }

  const deadItemId = op.kind === 'add' ? op.tempId : op.itemId;
  const dropped = state.queue.filter(
    (o) =>
      o.opId === opId ||
      (o.kind === 'check' && idEq(o.itemId, deadItemId)) ||
      (o.kind === 'add' && idEq(o.tempId, deadItemId))
  );
  const droppedIds = new Set(dropped.map((o) => o.opId));

  return {
    state: {
      ...state,
      items: state.items.filter((i) => !idEq(i.id, deadItemId)),
      queue: state.queue.filter((o) => !droppedIds.has(o.opId)),
      // An impossible-fail is authoritative NEWER knowledge of server
      // state (the server itself answered 404), exactly like an ack —
      // bumping keeps a poll whose read predates the 404 from
      // resurrecting the row this transition just removed and toasted
      // about (review finding F2).
      ackSeq: state.ackSeq + 1,
    },
    dropped,
  };
}

/**
 * Merge a poll response into local state — server truth for everything
 * EXCEPT what the queue still has an opinion about:
 *
 *  - discarded whole when `seqAtStart` (the ackSeq captured when the
 *    poll's request FIRED) no longer matches — an ack landed mid-flight,
 *    so this response may predate the acked write (`applied: false`);
 *  - a server row carrying a queued add op's client_key folds into its
 *    temp row: the add is provably already on the server, so the op is
 *    dropped and its dependents remapped to the real id (this also
 *    covers the op being inflight right now — the racing request is a
 *    dedupe no-op on the server, and its late response hits the
 *    tolerant unknown-opId path);
 *  - items with queued check ops render the queue's intent, not the
 *    server's (last-write-wins is still ahead: our write flushes next);
 *  - temp rows survive while their add op is queued;
 *  - a PENDING check op whose item the server no longer returns is
 *    dropped and reported (deleted elsewhere — the send would only 404);
 *    an INFLIGHT one is kept, its own response decides;
 *  - everything else, including row order, is the server's.
 */
export function mergeServerItems(state, serverItems, seqAtStart) {
  if (seqAtStart !== state.ackSeq) {
    return { state, dropped: [], applied: false };
  }

  const rows = (serverItems || []).map((r) => ({ ...r }));
  let queue = state.queue;
  let folded = false;

  for (const row of rows) {
    if (!row.client_key) {
      continue;
    }
    const addOp = queue.find((o) => o.kind === 'add' && o.clientKey === row.client_key);
    if (!addOp) {
      continue;
    }
    queue = queue
      .filter((o) => o.opId !== addOp.opId)
      .map((o) => (o.kind === 'check' && idEq(o.itemId, addOp.tempId) ? { ...o, itemId: row.id } : o));
    folded = true;
  }

  const items = rows.map((row) => overlayFromQueue(row, queue));

  for (const item of state.items) {
    if (!item.temp) {
      continue;
    }
    const stillQueued = queue.some((o) => o.kind === 'add' && idEq(o.tempId, item.id));
    if (stillQueued) {
      items.push(item);
    }
  }

  const serverIds = new Set(rows.map((r) => String(r.id)));
  const dropped = [];
  const nextQueue = [];
  for (const op of queue) {
    if (op.kind === 'check' && !serverIds.has(String(op.itemId))) {
      const isTempTarget = queue.some((o) => o.kind === 'add' && idEq(o.tempId, op.itemId));
      if (!isTempTarget && op.status === 'pending') {
        dropped.push(op);
        continue;
      }
    }
    nextQueue.push(op);
  }

  return {
    // A fold is the moment this state LEARNS its add landed server-side —
    // ack-equivalent knowledge, so it advances the stale-poll guard the
    // same way an ack does (review finding F3): a second, older read that
    // was still in flight across the fold must be discarded whole, or its
    // pre-add snapshot would drop the remapped checkoff as "deleted
    // elsewhere" and destroy the intent permanently.
    state: { ...state, items, queue: nextQueue, ackSeq: folded ? state.ackSeq + 1 : state.ackSeq },
    dropped,
    applied: true,
  };
}
